/**
 * The attributes ONE requirement declares on itself.
 *
 * The middle ground between an ordinary requirement and a typed one: a figure
 * this requirement carries - a delta-V, a TPM - as a real attribute with a
 * unit, without declaring a requirement type for the sake of a single
 * requirement. It is written inside the requirement:
 *
 *     attribute deltaV : ISQ::SpeedValue[0..1] = 1100 [SI::'m/s'];
 *
 * Outside the form for the reason sources are: declaring one is its own write
 * to its own route. The VALUE is an ordinary field - once declared it appears
 * in the form above, which is why a change here re-reads the schema as well as
 * the row.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ConfirmDialog from '../common/ConfirmDialog'
import HelpTip from '../common/HelpTip'
import NewAttributesTable from '../parts/NewAttributesTable'
import {
  attributePayload,
  attributeRowsProblem,
  emptyAttributeDraft,
} from '../parts/part-wizard-model'
import type { AttributeDraft } from '../parts/part-wizard-model'
import { ApiError, api } from '../../api'
import { useToasts } from '../../state/Toasts'
import { MONOSPACE } from '../../theme'
import type { AttributeTypeChoice } from '../../schema'

export const OWN_ATTRIBUTES_LABEL = 'Attributes of its own'

export interface OwnAttributesProps {
  /** The requirement's ID. */
  identifier: string
  /** `own_attributes` from the detail payload. */
  names: string[]
  allowed: boolean
  reason: string
  /** Called after a declaration or a removal has been written. */
  onChanged: () => void
}

export default function OwnAttributes({
  identifier,
  names,
  allowed,
  reason,
  onChanged,
}: OwnAttributesProps) {
  const toasts = useToasts()
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState('')
  const [busy, setBusy] = useState(false)

  const remove = async () => {
    setBusy(true)
    try {
      await api.removeRequirementAttribute(identifier, removing)
      toasts.succeeded(`${removing} removed from ${identifier}`)
      setRemoving('')
      onChanged()
    } catch (failure) {
      toasts.failed((failure as ApiError).message)
      setRemoving('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2">{OWN_ATTRIBUTES_LABEL}</Typography>
        <HelpTip
          label="What an attribute of its own is"
          title="A figure this one requirement carries, such as a technical performance measure, declared as a real attribute with a unit. Once declared it appears in the form above, where its value is stated. Use a requirement type instead when several requirements share the same attribute."
        />
      </Stack>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
        {names.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            None declared.
          </Typography>
        )}
        {names.map((name) => (
          <Chip
            key={name}
            label={name}
            size="small"
            sx={{ fontFamily: MONOSPACE }}
            onDelete={allowed ? () => setRemoving(name) : undefined}
          />
        ))}
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button size="small" onClick={() => setAdding(true)} disabled={!allowed}>
              Add attribute
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {adding && (
        <AddDialog
          identifier={identifier}
          onClose={() => setAdding(false)}
          onDeclared={() => {
            setAdding(false)
            onChanged()
          }}
        />
      )}

      <ConfirmDialog
        open={Boolean(removing)}
        title={`Remove ${removing}?`}
        message={`${identifier} will no longer declare ${removing}, and the value it states for it goes with it.`}
        confirmLabel="Remove"
        severity="error"
        busy={busy}
        onConfirm={() => void remove()}
        onCancel={() => setRemoving('')}
      />
    </Box>
  )
}

function AddDialog({
  identifier,
  onClose,
  onDeclared,
}: {
  identifier: string
  onClose: () => void
  onDeclared: () => void
}) {
  const toasts = useToasts()
  const [rows, setRows] = useState<AttributeDraft[]>([emptyAttributeDraft('attr-0')])
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState('')

  const [choices, setChoices] = useState<AttributeTypeChoice[]>([])
  useEffect(() => {
    let live = true
    api
      .attributeTypes()
      .then((answer) => live && setChoices(answer))
      .catch(() => live && setChoices([]))
    return () => {
      live = false
    }
  }, [])

  const declaring = attributePayload(rows)
  const problem = attributeRowsProblem(rows)

  const save = async () => {
    setBusy(true)
    setFailure('')
    try {
      await api.declareRequirementAttributes(
        identifier,
        // No '@RollsUp' on a requirement: nothing totals over it.
        declaring.map(({ rolls_up: _unused, ...rest }) => rest),
      )
      toasts.succeeded(
        `${declaring.map((entry) => entry.name).join(', ')} declared on ${identifier}`,
      )
      onDeclared()
    } catch (error) {
      setFailure((error as ApiError).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Add an attribute to <Box component="span" sx={{ fontFamily: MONOSPACE }}>{identifier}</Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {failure && <Alert severity="error">{failure}</Alert>}
          <NewAttributesTable
            rows={rows}
            choices={choices}
            onChange={setRows}
            disabled={busy}
            error={problem}
            showRollsUp={false}
          />
          <Typography variant="caption" color="text.secondary">
            The value is stated on the requirement once the attribute exists.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => void save()}
          disabled={busy || declaring.length === 0 || Boolean(problem)}
        >
          {busy ? 'Declaring…' : 'Declare'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
